/*
  Our work — the lightbox half of sections/showcase-grid.liquid.

  The grid is a contact sheet: every tile is cropped to one shape so the page cannot shift. This
  file is the other side of that bargain — it opens the original, uncropped, at its own shape.

  Nothing here builds a media URL. Each tile already carries a <template> holding exactly the
  element that belongs in the dialog: a full-size <img>, a <video>, or an <iframe> with its src
  parked in data-src. A template's contents are inert, so none of it is fetched until a tile is
  clicked. That is what keeps a page of twenty videos down to twenty poster images, and what keeps
  YouTube from reaching a visitor who never pressed play. Same trick as Dawn's <deferred-media>.

  Every tile is a real <a href> pointing at the file, so without JavaScript the page still works —
  the links just go to the media directly instead of opening in place.
*/
(function () {
  'use strict';

  function reducedMotion() {
    // Read at click time, not at load: the visitor can change this setting mid-visit, and on
    // desktop operating systems they do.
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init(root) {
    if (root.dataset.showcaseReady === 'true') return;
    root.dataset.showcaseReady = 'true';

    var dialog = root.querySelector('[data-showcase-lightbox]');
    var frame = root.querySelector('[data-showcase-frame]');
    var caption = root.querySelector('[data-showcase-caption]');
    var title = root.querySelector('[data-showcase-title]');
    var shopLink = root.querySelector('[data-showcase-link]');
    var prevBtn = root.querySelector('[data-showcase-prev]');
    var nextBtn = root.querySelector('[data-showcase-next]');
    var closeBtn = root.querySelector('[data-showcase-close]');
    var tiles = [].slice.call(root.querySelectorAll('[data-showcase-open]'));

    // <dialog> without showModal is the one case worth bailing on: the fallback would be a
    // non-modal box with no focus trap, which is worse than the plain link the tile already is.
    if (!dialog || !frame || !tiles.length || typeof dialog.showModal !== 'function') return;

    var index = -1;

    function templateFor(tile) {
      var item = tile.closest('.showcase__item');
      return item ? item.querySelector('[data-showcase-media]') : null;
    }

    function emptyFrame() {
      // Removing the node is what stops playback — an <iframe> keeps playing if it is merely
      // hidden, and a detached <video> releases its buffer.
      frame.textContent = '';
    }

    function show(i) {
      if (i < 0 || i >= tiles.length) return;
      index = i;

      var tile = tiles[i];
      var tpl = templateFor(tile);
      if (!tpl) return;

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      emptyFrame();

      var node = tpl.content.firstElementChild.cloneNode(true);
      var kind = tile.dataset.kind || 'photo';
      var text = tile.dataset.caption || '';

      // The frame takes the media's own ratio, so the dialog is the shape of the thing inside it.
      frame.style.setProperty('--sc-media-ratio', tile.dataset.ratio || '1.778');

      if (node.tagName === 'IFRAME') {
        var src = node.getAttribute('data-src') || '';
        // Autoplay only when the visitor has not asked for less motion. Either way the parameter
        // is added here rather than baked into the markup, so the choice is theirs and the
        // request only happens on a click they made.
        if (!reducedMotion()) src += (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
        node.removeAttribute('data-src');
        node.setAttribute('src', src);
      } else if (node.tagName === 'VIDEO') {
        node.setAttribute('controls', 'controls');
        node.preload = 'metadata';
      }

      frame.appendChild(node);

      if (caption) caption.textContent = text;
      if (title) {
        title.textContent =
          (kind === 'video' ? 'Video' : 'Photo') +
          ' ' + (i + 1) + ' of ' + tiles.length +
          (text ? '. ' + text : '');
      }

      if (shopLink) {
        if (tile.dataset.link) {
          shopLink.href = tile.dataset.link;
          shopLink.textContent = tile.dataset.linkLabel || 'Shop this';
          shopLink.hidden = false;
        } else {
          shopLink.hidden = true;
        }
      }

      var only = tiles.length < 2;
      if (prevBtn) prevBtn.hidden = only;
      if (nextBtn) nextBtn.hidden = only;

      if (node.tagName === 'VIDEO') {
        // The click that opened the dialog is the user gesture, so this play() is allowed with
        // sound. Under reduced motion the video is left sitting with its controls showing, which
        // is the only correct reading of "do not start moving on your own".
        if (!reducedMotion() && typeof node.play === 'function') {
          var started = node.play();
          if (started && typeof started.catch === 'function') started.catch(function () {});
        }
        // Focus the player either way: its controls are the first thing a keyboard user needs.
        node.focus();
      } else if (closeBtn) {
        closeBtn.focus();
      }
    }

    function open(i) {
      if (!dialog.open) dialog.showModal();
      show(i);
    }

    function step(by) {
      if (!tiles.length) return;
      show((index + by + tiles.length) % tiles.length);
    }

    tiles.forEach(function (tile, i) {
      tile.addEventListener('click', function (event) {
        // Let a modified click do what the visitor asked — open the file in a new tab.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        open(i);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', function () { dialog.close(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    // Click on the dialog itself, not its contents, means the backdrop.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      // A focused <video> owns its arrow keys — they seek by five seconds, and that is the only
      // keyboard scrub a player has. Stepping here would preventDefault the seek AND swap the
      // element out from under playback, so leave the keys alone whenever focus is on the media
      // itself. Prev/next stay reachable by Tab, and by arrow keys anywhere else in the dialog.
      if (frame && frame.contains(event.target)) return;

      event.preventDefault();
      step(event.key === 'ArrowLeft' ? -1 : 1);
    });

    // Fires for the close button, the backdrop and Escape alike, so cleanup lives in one place.
    dialog.addEventListener('close', function () {
      emptyFrame();
      if (index > -1 && tiles[index]) tiles[index].focus();
      index = -1;
    });
  }

  function boot(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    [].forEach.call(host.querySelectorAll('[data-showcase]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });

  // Selecting a block in the theme editor should show the owner the tile they just clicked.
  document.addEventListener('shopify:block:select', function (event) {
    var tile = event.target.querySelector('[data-showcase-open]');
    if (tile) tile.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
  });
})();
